import { useState } from 'react';
import WhiteLabelPaymentForm from './WhiteLabelPaymentForm';
import whiteLabelService from '../../services/whiteLabelService';
import { config } from '../../config';
import { useDispatch } from 'react-redux';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import './scss/WhiteLabelBilling.scss';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';

const WhiteLabelBilling = (props) => {
  const dispatch = useDispatch();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [error, setError] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  // Optimistic local trial state to avoid stale UI after starting trial
  const [optimisticTrial, setOptimisticTrial] = useState(null);

  const paymentsDisabled = config.DISABLE_WHITE_LABEL_PAYMENTS;
  const { onPurchaseSuccess, billingInfo = null, hasActiveSubscription = false, onCancelSuccess, usageInfo = null, trialInfo = null, onTrialStart } = props;


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePaymentSuccess = () => {
    setProcessing(true);
    setShowPaymentForm(false);

    // White label addon purchased successfully! Setting up your instance...

    if (onPurchaseSuccess) {
      onPurchaseSuccess();
    }

    // Keep processing state for a bit to show loading
    setTimeout(() => {
      setProcessing(false);
    }, 3000);
  };

  const handlePaymentError = (error) => {
    setError(error);
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      setError('');

      const response = await whiteLabelService.startTrial();

      if (response.success) {
        showSuccess(dispatch, 'White label trial started successfully! You have 90 days to explore all features.');

        // Optimistically reflect trial in UI immediately to avoid stale props
        const now = new Date();
        const end = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        setOptimisticTrial({
          isTrialing: true,
          trialStartDate: response?.trial?.trialStartDate || now.toISOString(),
          trialEndDate: response?.trial?.trialEndDate || end.toISOString(),
          trialUsed: true,
          daysRemaining: response?.trial?.daysRemaining ?? 90
        });

        // Wait a bit for backend to fully process
        await new Promise(resolve => setTimeout(resolve, 500));

        if (onTrialStart) {
          await onTrialStart();
        }

        // Reset loading state after refresh completes
        setStartingTrial(false);
      } else {
        throw new Error(response.message || 'Failed to start trial');
      }
    } catch (error) {
      console.error('Error starting trial:', error);
      const errorMessage = error.message || 'Failed to start trial. Please try again.';
      setError(errorMessage);
      showError(dispatch, errorMessage);
      setStartingTrial(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);
      setError('');

      const response = await whiteLabelService.cancelSubscription();

      if (response.success) {
        setShowCancelConfirm(false);
        showSuccess(dispatch, 'White label subscription canceled successfully');
        if (onCancelSuccess) {
          onCancelSuccess();
        }
      } else {
        throw new Error(response.message || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      const errorMessage = error.message || 'Failed to cancel subscription. Please try again or contact support.';
      setError(errorMessage);
      showError(dispatch, errorMessage);
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="white-label-billing">
      <div className="billing-card">
        <div className="billing-header">
          <div className="billing-icon">
            <span className="material-symbols-outlined">workspace_premium</span>
          </div>
          <div>
            <h2>White Label Addon</h2>
            <p>Personal addon - Create your own branded Zoobbe experience</p>
          </div>
        </div>

        {!hasActiveSubscription && (
          <>
            <div className="billing-features">
              <div className="feature">
                <CircleCheckOutlined size={20} color='var(--success-color)' />
                <span>Custom branding and logo</span>
              </div>
              <div className="feature">
                <CircleCheckOutlined size={20} color='var(--success-color)' />
                <span>Custom domain (app.yourbrand.com)</span>
              </div>
              <div className="feature">
                <CircleCheckOutlined size={20} color='var(--success-color)' />
                <span>Remove Zoobbe branding</span>
              </div>
              <div className="feature">
                <CircleCheckOutlined size={20} color='var(--success-color)' />
                <span>Public signup control</span>
              </div>
              <div className="feature">
                <CircleCheckOutlined size={20} color='var(--success-color)' />
                <span>Custom support email</span>
              </div>
            </div>

            <div className="billing-price">
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">9.99</span>
                <span className="period">/month</span>
              </div>
              <p className="price-note">Personal addon - Independent of workspace plans</p>
            </div>
          </>
        )}

        {error && (
          <div className="billing-error">
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        )}

        {paymentsDisabled ? (
          <div className="payment-disabled-notice">
            <span className="material-symbols-outlined">info</span>
            <div>
              <h3>Payment Currently Disabled</h3>
              <p>White label addon payments are temporarily disabled. Please contact support for assistance.</p>
            </div>
          </div>
        ) : (hasActiveSubscription || !!optimisticTrial) ? (
          showPaymentForm ? (
            <WhiteLabelPaymentForm
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            />
          ) : (
            <div className="subscription-management compact">
            <div className="subscription-status compact">
              <CircleCheckOutlined size={20} color='var(--success-color)' />
              <div>
                {(optimisticTrial?.isTrialing || trialInfo?.isTrialing) ? (
                  <>
                    <h3>White Label Trial Active</h3>
                    <div className="trial-badge">
                      <span className="material-symbols-outlined">schedule</span>
                      {(optimisticTrial?.daysRemaining ?? trialInfo?.daysRemaining)} days remaining
                    </div>
                    <div className="billing-details compact" style={{ marginTop: '12px' }}>
                      <div className="billing-row">
                        <span className="label">Trial started:</span>
                        <span className="value">{formatDate(optimisticTrial?.trialStartDate || trialInfo?.trialStartDate)}</span>
                      </div>
                      <div className="billing-row">
                        <span className="label">Trial ends:</span>
                        <span className="value">{formatDate(optimisticTrial?.trialEndDate || trialInfo?.trialEndDate)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>White Label Addon Active</h3>
                    {billingInfo && (
                      <div className="billing-details compact">
                        <div className="billing-row">
                          <span className="label">Billing:</span>
                          <span className="value">
                            ${billingInfo?.price || (billingInfo?.billingCycle === 'yearly' ? '95.99' : '9.99')}
                            /{billingInfo?.period || (billingInfo?.billingCycle === 'yearly' ? 'year' : 'month')}
                            {billingInfo?.billingCycle === 'yearly' && (
                              <span className="savings-badge">Save 20%</span>
                            )}
                          </span>
                        </div>
                        {billingInfo.nextBillingDate && (
                          <div className="billing-row">
                            <span className="label">Next billing:</span>
                            <span className="value">{formatDate(billingInfo.nextBillingDate)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>



            {(optimisticTrial?.isTrialing || trialInfo?.isTrialing) ? (
              <div className="trial-upgrade-section">
                <p className="upgrade-message">
                  Enjoying the trial? Upgrade to a paid plan to continue after your trial ends.
                </p>
                <button
                  className="purchase-button"
                  onClick={() => setShowPaymentForm(true)}
                >
                  <span className="material-symbols-outlined">credit_card</span>
                  Upgrade to Paid Plan
                </button>
              </div>
            ) : !showCancelConfirm ? (
              <button
                className="cancel-button"
                onClick={() => setShowCancelConfirm(true)}
              >
                <span className="material-symbols-outlined">cancel</span>
                Cancel Subscription
              </button>
            ) : (
              <div className="cancel-confirmation">
                <div className="cancel-warning">
                  <span className="material-symbols-outlined">warning</span>
                  <div>
                    <h4>Cancel White Label Subscription?</h4>
                    <p>This will immediately deactivate your white label instance and remove all custom branding. This action cannot be undone.</p>
                  </div>
                </div>
                <div className="cancel-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={canceling}
                  >
                    Keep Subscription
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleCancelSubscription}
                    disabled={canceling}
                  >
                    {canceling ? 'Canceling...' : 'Yes, Cancel Subscription'}
                  </button>
                </div>
              </div>
            )}
          </div>
          )
        ) : processing ? (
          <div className="processing-state">
            <div className="processing-spinner"></div>
            <h3>Processing Payment...</h3>
            <p>Please wait while we set up your white label addon. This may take a few moments.</p>
          </div>
        ) : startingTrial ? (
          <div className="processing-state">
            <div className="processing-spinner"></div>
            <h3>Starting Trial...</h3>
            <p>Please wait while we set up your 90-day trial. This may take a few moments.</p>
          </div>
        ) : !showPaymentForm ? (
          <>
            {!(trialInfo?.trialUsed || optimisticTrial?.trialUsed) && (
              <button
                className="trial-button"
                onClick={handleStartTrial}
                disabled={startingTrial}
              >
                <span className="material-symbols-outlined">schedule</span>
                Start 90-Day Free Trial
              </button>
            )}
            <button
              className="purchase-button"
              onClick={() => setShowPaymentForm(true)}
            >
              <span className="material-symbols-outlined">credit_card</span>
              Purchase White Label Addon
            </button>
          </>
        ) : (
          <WhiteLabelPaymentForm
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
          />
        )}

        {!(hasActiveSubscription || !!optimisticTrial) && (
          <div className="billing-footer">
            <p>
              <span className="material-symbols-outlined">security</span>
              Secure payment powered by Stripe
            </p>
            <p>
              <span className="material-symbols-outlined">support</span>
              Need help? Contact our support team
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhiteLabelBilling;
